import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/contexts/TranslationContext';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { MapPin, Navigation, Heart, MessageCircle, Share2, Bookmark, BookmarkCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { copyToClipboard } from '@/lib/utils';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',  
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface AdoptionPost {
  id: string;
  title: string;
  pet_name: string;
  species: string;
  breed?: string;
  age?: number;
  size?: string;
  gender?: string;
  character_traits?: string;
  health_info?: string;
  description?: string;
  location: string;
  adoption_conditions?: string;
  image_url?: string;
  status: string;
  requests_count: number;
  created_at: string;
  user_id: string;
  latitude?: number;
  longitude?: number;
}

interface AdoptionMapViewProps {
  onRequestAdoption: (post: AdoptionPost) => void;
}

const AdoptionMapView: React.FC<AdoptionMapViewProps> = ({ onRequestAdoption }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const markersLayer = useRef<L.LayerGroup | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [posts, setPosts] = useState<AdoptionPost[]>([]);
  const [selectedPost, setSelectedPost] = useState<AdoptionPost | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(true);
  const [savedPosts, setSavedPosts] = useState<string[]>([]);
  const [savingPost, setSavingPost] = useState(false);

  useEffect(() => {
    if (!mapContainer.current) return;

    // Initialize map
    if (!map.current) {
      map.current = L.map(mapContainer.current, {
        center: [41.9028, 12.4964], // Rome default
        zoom: 10,
        zoomControl: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map.current);

      markersLayer.current = L.layerGroup().addTo(map.current);
    }

    fetchAdoptionPosts();
    getCurrentLocation();
    fetchSavedPosts();

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
        markersLayer.current = null;
      }
    };
  }, []);

  const createAdoptionIcon = (species: string) => {
    const speciesEmojis: Record<string, string> = {
      'cane': '🐕',
      'gatto': '🐱',
      'coniglio': '🐰',
      'uccello': '🐦',
      'criceto': '🐹',
      'pesce': '🐠',
      'default': '🐾'
    };
    
    const emoji = speciesEmojis[species.toLowerCase()] || speciesEmojis.default;
    
    return L.divIcon({
      html: `
        <div style="
          background-color: #ec4899;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 3px solid white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          cursor: pointer;
        ">
          ${emoji}
        </div>
      `,
      className: 'custom-div-icon',
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      popupAnchor: [0, -18]
    });
  };

  const fetchAdoptionPosts = async () => {
    try {
      // First get adoption posts with location
      const { data: adoptionData, error: adoptionError } = await supabase
        .from('adoption_posts')
        .select('*')
        .eq('status', 'published')
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      if (adoptionError) throw adoptionError;

      // Then get profiles for each user
      const userIds = adoptionData?.map(post => post.user_id) || [];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, username, display_name, avatar_url')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      // Combine the data
      const data = adoptionData?.map(post => ({
        ...post,
        profiles: profilesData?.find(profile => profile.user_id === post.user_id)
      })) || [];

      const adoptionPosts = (data || []).map(post => ({
        id: post.id,
        title: post.title,
        pet_name: post.pet_name,
        species: post.species,
        breed: post.breed,
        age: post.age,
        size: post.size,
        gender: post.gender,
        character_traits: post.character_traits,
        health_info: post.health_info,
        description: post.description,
        location: post.location,
        adoption_conditions: post.adoption_conditions,
        image_url: post.image_url,
        status: post.status,
        requests_count: post.requests_count,
        created_at: post.created_at,
        user_id: post.user_id,
        latitude: post.latitude,
        longitude: post.longitude,
      }));

      setPosts(adoptionPosts);
      
      // Clear existing markers
      if (markersLayer.current) {
        markersLayer.current.clearLayers();
      }

      // Add markers for each post
      if (map.current && markersLayer.current) {
        adoptionPosts.forEach(post => {
          if (post.latitude && post.longitude && markersLayer.current) {
            const marker = L.marker(
              [post.latitude, post.longitude], 
              { icon: createAdoptionIcon(post.species) }
            );

            const popupContent = `
              <div style="min-width: 200px; padding: 10px;">
                <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold;">${post.pet_name}</h3>
                <p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">
                  ${post.species}${post.breed ? ` - ${post.breed}` : ''}
                </p>
                <p style="margin: 0 0 8px 0; font-size: 12px; color: #666;">
                  📍 ${post.location}
                </p>
                <button onclick="window.selectAdoptionPost('${post.id}')" style="
                  background: #3b82f6;
                  color: white;
                  border: none;
                  padding: 6px 12px;
                  border-radius: 6px;
                  font-size: 12px;
                  cursor: pointer;
                  width: 100%;
                ">
                  Vedi Dettagli
                </button>
              </div>
            `;

            marker.bindPopup(popupContent);
            marker.addTo(markersLayer.current);
          }
        });

        // Set up global function for selecting posts
        (window as any).selectAdoptionPost = (postId: string) => {
          const post = adoptionPosts.find(p => p.id === postId);
          if (post) {
            setSelectedPost(post);
            setShowDetails(true);
            // Close any open popups when showing details
            if (map.current) {
              map.current.closePopup();
            }
          }
        };

        // Fit map to show all markers
        if (adoptionPosts.length > 0) {
          const group = new L.FeatureGroup(
            adoptionPosts
              .filter(post => post.latitude && post.longitude)
              .map(post => L.marker([post.latitude!, post.longitude!]))
          );
          
          if (userLocation) {
            group.addLayer(L.marker([userLocation[0], userLocation[1]]));
          }
          
          map.current.fitBounds(group.getBounds().pad(0.1));
        }
      }
    } catch (error) {
      console.error('Error fetching adoption posts:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare i post di adozione",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation([latitude, longitude]);
        
        if (map.current && markersLayer.current) {
          // Add user location marker
          const userMarker = L.marker([latitude, longitude], {
            icon: L.divIcon({
              html: `
                <div style="
                  background-color: #3b82f6;
                  width: 20px;
                  height: 20px;
                  border-radius: 50%;
                  border: 3px solid white;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                "></div>
              `,
              className: 'user-location-icon',
              iconSize: [20, 20],
              iconAnchor: [10, 10]
            })
          });
          
          userMarker.bindPopup('📍 La tua posizione').addTo(markersLayer.current);
          
          // Center map on user location
          map.current.setView([latitude, longitude], 12);
        }
      },
      (error) => {
        console.warn('Geolocation error:', error);
      }
    );
  };

  const fetchSavedPosts = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('adoption_saves')
        .select('adoption_post_id')
        .eq('user_id', user.id);

      if (error) throw error;

      setSavedPosts(data?.map(save => save.adoption_post_id) || []);
    } catch (error) {
      console.error('Error fetching saved posts:', error);
    }
  };

  const handleSaveAdoption = async () => {
    if (!user?.id || !selectedPost) return;

    const isSaved = savedPosts.includes(selectedPost.id);
    setSavingPost(true);

    try {
      if (isSaved) {
        const { error } = await supabase
          .from('adoption_saves')
          .delete()
          .eq('user_id', user.id)
          .eq('adoption_post_id', selectedPost.id);

        if (error) throw error;

        setSavedPosts(prev => prev.filter(id => id !== selectedPost.id));
        toast({
          title: "Annuncio rimosso",
          description: "L'annuncio è stato rimosso dai salvati",
        });
      } else {
        const { error } = await supabase
          .from('adoption_saves')
          .insert({
            user_id: user.id,
            adoption_post_id: selectedPost.id
          });

        if (error) throw error;

        setSavedPosts(prev => [...prev, selectedPost.id]);
        toast({
          title: "Annuncio salvato",
          description: "L'annuncio è stato aggiunto ai salvati",
        });
      }
    } catch (error) {
      console.error('Error saving/unsaving post:', error);
      toast({
        title: "Errore",
        description: "Impossibile salvare l'annuncio",
        variant: "destructive"
      });
    } finally {
      setSavingPost(false);
    }
  };

  const handleContactOwner = async (post: AdoptionPost) => {
    if (!user?.id) {
      toast({
        title: "Accesso richiesto",
        description: "Devi essere autenticato per contattare il proprietario",
        variant: "destructive"
      });
      return;
    }

    try {
      // Check if conversation already exists
      const { data: existingConversation, error: checkError } = await supabase
        .from('conversations')
        .select('id')
        .eq('type', 'direct')
        .eq('created_by', user.id)
        .limit(1)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      let conversationId = existingConversation?.id;

      if (!conversationId) {
        // Create new conversation
        const { data: newConversation, error: createError } = await supabase
          .from('conversations')
          .insert({
            type: 'direct',
            created_by: user.id
          })
          .select('id')
          .single();

        if (createError) throw createError;
        conversationId = newConversation.id;

        // Add participants
        const { error: participantsError } = await supabase
          .from('conversation_participants')
          .insert([
            { conversation_id: conversationId, user_id: user.id },
            { conversation_id: conversationId, user_id: post.user_id }
          ]);

        if (participantsError) throw participantsError;
      }

      // Navigate to chat
      window.location.href = `/app/chat/${conversationId}`;
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast({
        title: "Errore",
        description: "Impossibile avviare la conversazione",
        variant: "destructive"
      });
    }
  };

  const handleShare = () => {
    if (!selectedPost) return;
    
    const shareUrl = `${window.location.origin}/app/adoption?post=${selectedPost.id}`;
    
    if (navigator.share) {
      navigator.share({
        title: `Adozione: ${selectedPost.pet_name}`,
        text: `${selectedPost.title} - ${selectedPost.description?.substring(0, 100)}...`,
        url: shareUrl,
      });
    } else {
      copyToClipboard(shareUrl);
      toast({
        title: "Link copiato",
        description: "Il link all'annuncio è stato copiato negli appunti",
      });
    }
  };

  const centerOnUserLocation = async () => {
    if (!map.current) {
      toast({
        title: "Errore",
        description: "Mappa non ancora caricata",
        variant: "destructive"
      });
      return;
    }

    // Mostra feedback di caricamento
    toast({
      title: "📍 Rilevamento posizione...",
      description: "Ottenimento della tua posizione in corso",
    });

    try {
      // Usa la funzione migliorata di geolocalizzazione
      const { getCurrentLocationWithRetry } = await import('@/utils/geolocationUtils');
      const location = await getCurrentLocationWithRetry(2);

      if (!location) {
        return; // L'errore è già gestito dalla funzione
      }

      setUserLocation([location.lat, location.lng]);
      
      if (map.current && markersLayer.current) {
        // Rimuovi marker esistente della posizione utente
        markersLayer.current.eachLayer((layer) => {
          if (layer instanceof L.Marker && (layer as any).options.icon.options.className === 'user-location-icon') {
            markersLayer.current?.removeLayer(layer);
          }
        });
        
        // Aggiungi nuovo marker della posizione utente con precisione
        const accuracy = location.accuracy ? Math.round(location.accuracy) : 'sconosciuta';
        const userMarker = L.marker([location.lat, location.lng], {
          icon: L.divIcon({
            html: `<div style="background-color: #3b82f6; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>`,
            className: 'user-location-icon',
            iconSize: [20, 20],
            iconAnchor: [10, 10]
          })
        });
        
        userMarker.bindPopup(`📍 La tua posizione<br>Precisione: ±${accuracy}m`).addTo(markersLayer.current);
        
        // Aggiungi cerchio di precisione se disponibile
        if (location.accuracy && location.accuracy < 100) {
          L.circle([location.lat, location.lng], {
            radius: location.accuracy,
            color: '#3b82f6',
            fillColor: '#3b82f6',
            fillOpacity: 0.1,
            weight: 1
          }).addTo(markersLayer.current);
        }
        
        // Centra la mappa sulla posizione attuale
        map.current.setView([location.lat, location.lng], 14, { animate: true, duration: 0.5 });
      }
    } catch (error) {
      console.error('Error getting location:', error);
      toast({
        title: "❌ Errore Geolocalizzazione",
        description: "Errore imprevisto nel rilevamento della posizione",
        variant: "destructive"
      });
    }
  };

  const isPostSaved = selectedPost ? savedPosts.includes(selectedPost.id) : false;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
      {/* Map Container - Takes 2/3 of space on large screens */}
      <div className="lg:col-span-2 space-y-4">
        <div className="relative">
          <div
            ref={mapContainer}
            className="w-full h-[500px] lg:h-[600px] rounded-lg border border-border"
            style={{ zIndex: 1 }}
          />
          
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-lg z-[999]">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          )}

          <Button
            onClick={centerOnUserLocation}
            className="absolute top-4 right-4 z-10 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md border border-white/20"
            size="sm"
            style={{ 
              minWidth: '120px',
              fontSize: '12px',
              fontWeight: '500',
              borderRadius: '6px',
              maxWidth: 'calc(100% - 32px)'
            }}
          >
            <Navigation className="w-4 h-4 mr-1.5" />
            La mia posizione
          </Button>
        </div>

        {/* Legend */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 text-sm">{t('adoption.legend')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-pink-500 flex items-center justify-center text-white">
                  🐾
                </div>
                <span>{t('adoption.petForAdoption')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                <span>{t('adoption.yourLocation')}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Details Panel - Takes 1/3 of space on large screens, full width on mobile */}
      <div className="lg:col-span-1">
        {selectedPost ? (
          <Card className="h-fit sticky top-4">
            <CardHeader>
              <CardTitle className="text-lg">{t('adoption.details')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start space-x-3">
                <Avatar className="w-16 h-16 flex-shrink-0">
                  <AvatarImage src={selectedPost.image_url} alt={selectedPost.pet_name} />
                  <AvatarFallback>{selectedPost.pet_name[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-2 min-w-0">
                  <div>
                    <h3 className="font-semibold truncate">{selectedPost.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {selectedPost.pet_name} • {selectedPost.species}
                      {selectedPost.breed && ` • ${selectedPost.breed}`}
                    </p>
                    <div className="flex items-center space-x-1 mt-1">
                      <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                      <span className="text-xs text-muted-foreground truncate">{selectedPost.location}</span>
                    </div>
                  </div>
                </div>
              </div>

              {selectedPost.description && (
                <div>
                  <h4 className="font-medium text-sm mb-2">Descrizione</h4>
                  <p className="text-sm text-muted-foreground">
                    {selectedPost.description}
                  </p>
                </div>
              )}

              {selectedPost.character_traits && (
                <div>
                  <h4 className="font-medium text-sm mb-2">Caratteristiche</h4>
                  <p className="text-sm text-muted-foreground">
                    {selectedPost.character_traits}
                  </p>
                </div>
              )}

              <div className="space-y-3 pt-4">
                {/* Primary action buttons */}
                {user?.id !== selectedPost.user_id && (
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      onClick={() => {
                        onRequestAdoption(selectedPost);
                        setSelectedPost(null);
                        setShowDetails(false);
                      }}
                      size="sm"
                      className="bg-primary hover:bg-primary/90 transition-all duration-200"
                    >
                      <Heart className="w-4 h-4 mr-1" />
                      Adotta
                    </Button>
                    <Button
                      onClick={() => handleContactOwner(selectedPost)}
                      size="sm"
                      variant="secondary"
                      className="border-primary/20 hover:border-primary/40 transition-all duration-200"
                    >
                      <MessageCircle className="w-4 h-4 mr-1" />
                      Contatta
                    </Button>
                    <Button
                      onClick={handleSaveAdoption}
                      disabled={savingPost}
                      size="sm"
                      variant="outline"
                      className={`transition-all duration-200 ${
                        isPostSaved 
                          ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20' 
                          : 'border-muted-foreground/30 hover:border-primary/40'
                      }`}
                    >
                      {isPostSaved ? <BookmarkCheck className="w-4 h-4 mr-1" /> : <Bookmark className="w-4 h-4 mr-1" />}
                      {isPostSaved ? 'Salvato' : 'Salva'}
                    </Button>
                  </div>
                )}
                
                {/* Secondary action buttons */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleShare}
                    className="text-xs hover:bg-white/10"
                  >
                    <Share2 className="w-3 h-3 mr-1" />
                    Condividi
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedPost(null);
                      setShowDetails(false);
                    }}
                  >
                    Chiudi Dettagli
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="h-fit">
            <CardContent className="p-6 text-center">
              <div className="text-muted-foreground">
                <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  Clicca su un marker nella mappa per vedere i dettagli dell'adozione
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdoptionMapView;